import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { httpGet, httpPost } from "../hooks/http";
import { UserContext } from "../UserContext";

export const useClientPayments = (
  clientId,
  businessLocationId,
  payments,
  userPreferredLanguage
) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentUrl, setPaymentUrl] = useState(null);
  const [paymentId, setPaymentId] = useState(null);
  const [transaction, setTransaction] = useState(null);
  const { access_token, tppPaySettings } = useContext(UserContext);
  const timeoutIdRef = useRef(null);

  let settings = tppPaySettings.slice(-1).pop()?.settings;
  if (businessLocationId) {
    settings = tppPaySettings.find(
      (g) => g.id === businessLocationId
    )?.settings;
  }

  const checkPaymentResponse = useCallback(
    async (stopped) => {
      if (!paymentId) {
        return;
      }
      var response = await httpGet(
        `clientPortal/clients/${clientId}/payments/checkresponse/${paymentId}`,
        access_token
      );

      const {
        response: { transactionDetail },
      } = response;

      if (!transactionDetail?.responseCode?.length) {
        if (!stopped) {
          timeoutIdRef.current = setTimeout(
            () => checkPaymentResponse(stopped),
            5000
          );
        }
        return;
      }
      setTransaction(transactionDetail);
    },
    [access_token, clientId, paymentId]
  );

  useEffect(() => {
    if (!userPreferredLanguage) {
      return;
    }
    setIsLoading(true);
    let url = `clientPortal/clients/${clientId}/payments/getpaymentformurl?userLanguage=${userPreferredLanguage}`;
    if (businessLocationId) {
      url += `&businessLocationId=${businessLocationId}`;
    }
    url += `&merchantLanguage=${
      settings?.merchantPreferredLanguage || "en-Us"
    }`;
    httpPost(url, payments, access_token).then(
      (response) => {
        const { loaded, response: results } = response;
        if (!loaded) {
          return;
        }
        setIsLoading(false);
        setPaymentUrl(results?.hostedFormUrl);
        setPaymentId(results?.paymentId);
      },
      (error) => {
        setError("Could not load payment form");
      }
    );
  }, [
    access_token,
    businessLocationId,
    clientId,
    payments,
    settings?.merchantPreferredLanguage,
    userPreferredLanguage,
  ]);

  useEffect(() => {
    let stopped = false;
    checkPaymentResponse(stopped);
    return () => {
      stopped = true;
      clearTimeout(timeoutIdRef.current);
    };
  }, [paymentId, checkPaymentResponse]);

  return {
    isLoading,
    paymentUrl,
    paymentId,
    transaction,
    settings,
    error,
  };
};
